import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {
  login,
  LoginRequest,
  LoginResponse,
  ResetPasswordRequest,
  signUp,
  SignUpRequest,
  resetPassword,
  verifySecurityCode,
  SecurityCodePayload,
  NewPasswordData,
  setNewPassword,
} from '@app/api/auth.api'
import { acceptConsent, AcceptConsentPayload } from '@app/api/consent.api'
import { getProfileData, setProfileUser } from '@app/store/slices/profile-slice'
import { deleteToken, deleteUser, persistToken, readToken } from '@app/store/services/local-storage.service'
import { notificationController } from '@app/controllers/notification-controller'
import { t } from 'i18next'

import { getErrorMessage } from '@/utils/utils'
import { resetStore } from '@/store/actions/reset'

export type AuthSlice = {
  token: string | null
  isLoading: boolean
  error?: string | undefined
  consentRequired: boolean
}

const initialState: AuthSlice = {
  isLoading: false,
  error: '',
  token: readToken(),
  consentRequired: false,
}

export const doLogin = createAsyncThunk<LoginResponse, LoginRequest>(
  'auth/doLogin',
  async (loginPayload: LoginRequest, { dispatch, rejectWithValue }) => {
    try {
      const res = await login(loginPayload)
      dispatch(getProfileData(res.id))
      persistToken(res.token)
      setProfileUser({ id: res.id })
      return res
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const doLogout = createAsyncThunk('auth/doLogout', async (_, { dispatch }) => {
  const hadToken = !!readToken()

  deleteToken()
  deleteUser()
  dispatch(resetStore())

  if (hadToken) {
    notificationController.success({ message: t('beNotifications.success.logoutSuccess') })
  }
})

// not used yet
export const doSignUp = createAsyncThunk('auth/doSignUp', async (signUpPayload: SignUpRequest, { rejectWithValue }) => {
  try {
    return await signUp(signUpPayload)
  } catch (error) {
    return rejectWithValue(getErrorMessage(error))
  }
})

export const doResetPassword = createAsyncThunk(
  'auth/doResetPassword',
  async (resetPassPayload: ResetPasswordRequest, { rejectWithValue }) => {
    try {
      return await resetPassword(resetPassPayload)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const doVerifySecurityCode = createAsyncThunk(
  'auth/doVerifySecurityCode',
  async (securityCodePayload: SecurityCodePayload, { rejectWithValue }) => {
    try {
      return await verifySecurityCode(securityCodePayload)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const doSetNewPassword = createAsyncThunk(
  'auth/doSetNewPassword',
  async (newPasswordData: NewPasswordData, { rejectWithValue }) => {
    try {
      return await setNewPassword(newPasswordData)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const doAcceptConsent = createAsyncThunk(
  'auth/doAcceptConsent',
  async (payload: AcceptConsentPayload, { rejectWithValue }) => {
    try {
      return await acceptConsent(payload)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    requireConsent(state) {
      state.consentRequired = true
    },
  },
  extraReducers: (builder) => {
    builder.addCase(doLogin.pending, (state, action) => {
      state.error = undefined
      state.isLoading = true
    })
    builder.addCase(doLogin.fulfilled, (state, action) => {
      const { token, consentRequired } = action.payload as any
      state.token = token
      state.consentRequired = !!consentRequired
      state.isLoading = false
    })
    builder.addCase(doLogin.rejected, (state, action) => {
      state.isLoading = false
      state.error = action.payload as string
    })
    builder.addCase(doLogout.fulfilled, (state) => {
      state.token = ''
      state.consentRequired = false
    })
    builder.addCase(doAcceptConsent.fulfilled, (state) => {
      state.consentRequired = false
    })

    builder.addCase(resetStore, () => initialState)
  },
})

export default authSlice.reducer
export const { requireConsent } = authSlice.actions
